/**
 * SectionEditor Component
 *
 * Inline editor for a single script section within the ScriptPreviewModal.
 * Supports editing title, narration text, reordering, and deletion.
 */

import { memo, useCallback, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { EditableSection } from '@/hooks/useScriptEditState';

interface SectionEditorProps {
  section: EditableSection;
  index: number;
  onUpdate: (index: number, changes: Partial<EditableSection>) => void;
  onDelete: (index: number) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
  canDelete: boolean;
}

export const SectionEditor = memo(function SectionEditor({
  section,
  index,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  canDelete,
}: SectionEditorProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(80, textarea.scrollHeight)}px`;
    }
  }, [section.narration_text]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(index, { title: e.target.value });
    },
    [index, onUpdate]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(index, { narration_text: e.target.value });
    },
    [index, onUpdate]
  );

  const handleToggleExpand = useCallback(() => {
    onUpdate(index, { isExpanded: !section.isExpanded });
  }, [index, section.isExpanded, onUpdate]);

  const wordCount = section.narration_text.trim()
    ? section.narration_text.trim().split(/\s+/).length
    : 0;

  return (
    <div
      className={`rounded-xl overflow-hidden transition-all duration-200 ${section.isExpanded ? 'bg-secondary-800/60 ring-1 ring-secondary-600/50' : 'bg-secondary-800/30 hover:bg-secondary-800/50'}`}
    >
      {/* Header - Minimal: badge + title + chevron */}
      <button
        type="button"
        onClick={handleToggleExpand}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600/80 text-white text-xs font-semibold flex items-center justify-center">
          {index + 1}
        </span>
        <span className="text-sm font-medium text-secondary-200 truncate flex-1">
          {section.title}
        </span>
        {!section.isExpanded && (
          <span className="text-[11px] text-secondary-500 flex-shrink-0">
            {wordCount} {t('scriptPreview.words', { defaultValue: 'words' })}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-secondary-400 flex-shrink-0 transition-transform duration-200 ${section.isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Body - Expanded content */}
      {section.isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Title input */}
          <div>
            <label className="text-[11px] font-medium text-secondary-500 uppercase tracking-wider mb-1.5 block">
              {t('scriptPreview.sectionTitle', { defaultValue: 'Section title' })}
            </label>
            <input
              type="text"
              value={section.title}
              onChange={handleTitleChange}
              placeholder={t('scriptPreview.sectionTitle', { defaultValue: 'Section title' })}
              className="w-full bg-secondary-700/50 border border-secondary-600/60 rounded-lg px-3 py-2.5 text-sm text-secondary-100 placeholder-secondary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>

          {/* Narration textarea */}
          <div className="relative">
            <label className="text-[11px] font-medium text-secondary-500 uppercase tracking-wider mb-1.5 block">
              {t('scriptPreview.sectionNarration', { defaultValue: 'Narration text' })}
            </label>
            <textarea
              ref={textareaRef}
              value={section.narration_text}
              onChange={handleTextChange}
              placeholder={t('scriptPreview.sectionNarration', { defaultValue: 'Narration text' })}
              className="w-full bg-secondary-700/50 border border-secondary-600/60 rounded-lg px-3 py-2.5 text-sm text-secondary-200 placeholder-secondary-500 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors min-h-[120px]"
            />
            {/* Word count badge */}
            <span className="absolute bottom-2.5 right-2.5 text-[10px] text-secondary-500 bg-secondary-800/80 px-2 py-0.5 rounded-full">
              {wordCount} {t('scriptPreview.words', { defaultValue: 'words' })}
            </span>
          </div>

          {/* Action buttons row - at bottom of expanded body */}
          <div className="flex items-center justify-end gap-1 pt-1 border-t border-secondary-700/40">
            {onMoveUp && (
              <button
                type="button"
                onClick={() => onMoveUp(index)}
                disabled={isFirst}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-secondary-400 hover:text-secondary-200 hover:bg-secondary-700/50 disabled:opacity-25 disabled:hover:bg-transparent transition-colors min-h-[32px]"
                title={t('scriptPreview.moveUp', { defaultValue: 'Move up' })}
              >
                <ChevronUp className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('scriptPreview.moveUp', { defaultValue: 'Move up' })}
                </span>
              </button>
            )}
            {onMoveDown && (
              <button
                type="button"
                onClick={() => onMoveDown(index)}
                disabled={isLast}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-secondary-400 hover:text-secondary-200 hover:bg-secondary-700/50 disabled:opacity-25 disabled:hover:bg-transparent transition-colors min-h-[32px]"
                title={t('scriptPreview.moveDown', { defaultValue: 'Move down' })}
              >
                <ChevronDown className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('scriptPreview.moveDown', { defaultValue: 'Move down' })}
                </span>
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(index)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-red-400/80 hover:text-red-300 hover:bg-red-900/20 transition-colors min-h-[32px] ml-1"
                title={t('scriptPreview.deleteSection', { defaultValue: 'Remove' })}
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('scriptPreview.deleteSection', { defaultValue: 'Remove' })}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
